import { Radio, RadioGroup } from '@ds';
import { AdminFormRow, ConditionalRender } from '@ui';
import { memo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { ControlledTextInput } from '../../components/inputs/ControlledTextInput';
import { DriverAffiliationType, DriverFormType } from '@/api/apis/resources';
import { BANNED_DRIVERS_AFFILATION_PREFERENCES } from '../config';
import { TxCenterProviderAutocomplete } from './TxCenterProviderAutocomplete';

type AffiliationSectionProps = {
  isReadOnly?: boolean;
};

export const AffiliationSection = memo(({ isReadOnly = false }: AffiliationSectionProps) => {
  const { control, setValue } = useFormContext<DriverFormType>();

  const affiliationType = useWatch({ control, name: 'affiliationType' });

  const isAffiliationTmdx = affiliationType === DriverAffiliationType.TMDX;
  const isAffiliationTxCenter = affiliationType === DriverAffiliationType.TXCenterProvider;

  const onAffiliationChange = (newAffiliationType: DriverAffiliationType) => {
    switch (newAffiliationType) {
      case DriverAffiliationType.TXCenterProvider:
        setValue('companyName', '');
        break;
      case DriverAffiliationType.TMDX:
        setValue('txCenterProviderId', '');
        setValue('txCenterProviderName', '');
        break;
      case DriverAffiliationType.None:
      default:
        setValue('txCenterProviderId', '');
        setValue('txCenterProviderName', '');
        setValue('companyName', '');
        break;
    }
  };

  return (
    <>
      <AdminFormRow title="Affiliation" gap="md">
        <Controller
          name="affiliationType"
          control={control}
          render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
            <RadioGroup
              orientation="horizontal"
              value={value}
              isInvalid={invalid}
              errorMessage={error?.message}
              onValueChange={(newValue) => {
                onChange(newValue as DriverAffiliationType);
                onAffiliationChange(newValue as DriverAffiliationType);
              }}
              {...field}
            >
              <div className="flex flex-row flex-wrap gap-2">
                {BANNED_DRIVERS_AFFILATION_PREFERENCES.map((affiliation) => (
                  <Radio isDisabled={isReadOnly} key={affiliation.value} value={affiliation.value} size="md">
                    {affiliation.name}
                  </Radio>
                ))}
              </div>
            </RadioGroup>
          )}
        />
      </AdminFormRow>
      <ConditionalRender show={isAffiliationTmdx}>
        <AdminFormRow title="" gap="md">
          <ControlledTextInput
            name="companyName"
            aria-label="Company name"
            placeholder="Company name"
            isDisabled={isReadOnly}
          />
        </AdminFormRow>
      </ConditionalRender>
      <ConditionalRender show={isAffiliationTxCenter}>
        <AdminFormRow title="" gap="md">
          <TxCenterProviderAutocomplete isDisabled={isReadOnly} />
        </AdminFormRow>
      </ConditionalRender>
    </>
  );
});
